import { ICellRendererParams } from 'ag-grid-community';
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import { AlertApiClientService } from '../../services/alert-api-client.service';
import { sanitizeEmoji } from '../../utils/sanitize-input.util';

@Component({
  selector: 'app-title-renderer',
  templateUrl: './title-renderer.component.html',
  styleUrls: ['./title-renderer.component.scss'],
  standalone: false,
})
export class TitleRendererComponent implements OnDestroy {
  private readonly alertApiClientService = inject(AlertApiClientService);

  @ViewChild('titleInput') titleInput!: ElementRef;
  @ViewChild('tooltip') errorTooltip!: MatTooltip;

  params!: ICellRendererParams;
  title!: string;
  isEditing = false;
  isSystemGenerated = false;
  isError = false;
  isPanelOpen = false;
  tooltipMessage!: string;
  private hasBeenDestroyed = false;
  private focusTimeoutId?: ReturnType<typeof setTimeout>;
  private tooltipTimeoutId?: ReturnType<typeof setTimeout>;
  private panelToggleTimeoutId?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    this.hasBeenDestroyed = true;
    if (this.focusTimeoutId) {
      clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = undefined;
    }
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
    if (this.panelToggleTimeoutId) {
      clearTimeout(this.panelToggleTimeoutId);
      this.panelToggleTimeoutId = undefined;
    }
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.title = params.data.title;
    this.isSystemGenerated = params.data.systemGenerated;

    // Auto-focus Title only for duplicated alerts
    if (params.data._focusOnDuplicate) {
      this.focusTimeoutId = setTimeout(() => {
        if (!this.hasBeenDestroyed) {
          this.startEdit();
          params.data._focusOnDuplicate = false;
        }
      }, 100);
    }
    this.isPanelOpen = !!params.data._panelOpen;
  }

  togglePanel(event: Event): void {
    event.stopPropagation();

    this.panelToggleTimeoutId = setTimeout(() => {
      const api = this.params.api;
      const node = this.params.node;
      const willOpen = !node.data._panelOpen;
      api.forEachNode((n) => {
        if (n.data && n !== node) {
          n.data._panelOpen = false;
          const panelId = n.data.id + '__panel';
          const panelNode = api.getRowNode(panelId);
          if (panelNode) {
            api.applyTransaction({
              remove: [{ ...panelNode.data, id: panelId }],
            });
          }
        }
      });

      if (willOpen) {
        node.data._panelOpen = true;
        const panelId = node.data.id + '__panel';
        if (!api.getRowNode(panelId)) {
          const panelData = {
            ...node.data,
            id: panelId,
            originalId: node.data.id,
            _panelRow: true,
          };
          api.applyTransaction({
            add: [panelData],
            addIndex: (node.rowIndex ?? 0) + 1,
          });
        }
      } else {
        node.data._panelOpen = false;
        const panelId = node.data.id + '__panel';
        const panelNode = api.getRowNode(panelId);
        if (panelNode) {
          api.applyTransaction({ remove: [panelNode.data] });
        }
      }

      if (!this.hasBeenDestroyed) {
        this.isPanelOpen = willOpen;
      }
      api.refreshCells({ force: true });
    }, 0);
  }

  startEdit(): void {
    if (!this.isSystemGenerated) {
      this.isEditing = true;
      this.focusTimeoutId = setTimeout(() => {
        if (!this.hasBeenDestroyed && this.titleInput?.nativeElement) {
          this.titleInput.nativeElement.focus();
        }
      });
    }
  }

  saveEdit(event: any): void {
    event.preventDefault();
    if (event.key === 'Enter' || event.type === 'mousedown') {
      if (this.validateInput()) {
        this.isEditing = false;
        this.updateTitle();
      }
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  cancelEdit(): void {
    this.hideErrorTooltip();
    this.isEditing = false;
    this.title = this.params.data.title;
  }

  private updateTitle(): void {
    if (this.params.data.title !== this.title?.trim()) {
      this.params.data.title = this.title;
      this.alertApiClientService.updateAlert(this.params.data).subscribe({
        next: (updated) => {
          this.params.node.setData(updated);
        },
        error: () => {
          this.cancelEdit();
        },
      });
    } else {
      this.cancelEdit();
    }
  }

  private validateInput(): boolean {
    const trimmedTitle = this.title?.trim();
    if (!trimmedTitle) {
      return false;
    }
    if (this.isTitleDuplicate()) {
      this.showErrorTooltip(
        'That title is already being used for another Alert.',
      );
      return false;
    } else {
      this.hideErrorTooltip();
      return true;
    }
  }

  private showErrorTooltip(message: string): void {
    this.isError = true;
    this.tooltipMessage = message;
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.disabled = false;
    this.tooltipTimeoutId = setTimeout(() => {
      if (!this.hasBeenDestroyed && this.errorTooltip) {
        this.errorTooltip.show();
      }
    });
  }

  private hideErrorTooltip(): void {
    this.isError = false;
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.hide();
    this.errorTooltip.disabled = true;
  }

  private isTitleDuplicate(): boolean {
    const allTitles = this.params.api
      .getRenderedNodes()
      .map((node) => node.data.title?.trim()?.toLowerCase())
      .filter((t) => t !== this.params.data.title.trim()?.toLowerCase());
    return allTitles.includes(this.title?.trim()?.toLowerCase());
  }

  handleInput(event: Event): void {
    this.title = sanitizeEmoji(event);
    const trimmedTitle = this.title?.trim();
    if (trimmedTitle.length >= 100) {
      this.showErrorTooltip('Title cannot exceed 100 characters.');
    } else if (this.isError) {
      this.hideErrorTooltip();
    }
  }
}
